import { useEffect, useState } from "react";
import { Wallet } from "lucide-react";

interface CashState {
  cash_disponible: number;
  cash_maj?: string;
  cash_source?: string;
}

const fmtEur = (n: number) => {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)} M€`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)}k €`;
  return `${n.toLocaleString("fr-FR")} €`;
};

/**
 * Live cash banner — reads `cash_disponible` from /api/external/performance
 * (which proxies the Cloudflare-tunneled API). Inserted at the top of the
 * "Plan de trésorerie" widget on the dashboard.
 */
export function LiveCashBanner() {
  const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
  const [cash, setCash] = useState<CashState | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        const r = await fetch(`${BACKEND}/api/external/performance`, { credentials: "include" });
        if (r.ok) setCash(await r.json());
      } catch {
        /* silent */
      }
    };
    load();
    const i = setInterval(load, 60000);
    return () => clearInterval(i);
  }, [BACKEND]);

  if (!cash || !cash.cash_disponible) return null;

  return (
    <div
      className="mb-3 flex items-center justify-between gap-3 rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-3 py-2"
      data-testid="live-cash-banner"
    >
      <div className="flex items-center gap-2 min-w-0">
        <Wallet className="h-4 w-4 text-emerald-400 flex-shrink-0" />
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-wider text-emerald-300/80">
            Cash disponible — Live
          </div>
          <div className="text-xl font-bold text-emerald-400" data-testid="cash-disponible-value">
            {fmtEur(cash.cash_disponible)}
          </div>
        </div>
      </div>
      <div className="text-right text-[10px] text-muted-foreground">
        {cash.cash_maj && <div>Mis à jour : {cash.cash_maj}</div>}
        {cash.cash_source && <div>Source : {cash.cash_source}</div>}
      </div>
    </div>
  );
}
